import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {fetchItemsPage, fetchDataUnauth} from '../../http/itemsAPI';
import {CATEGORIES} from '../../http/ApiEndpoints';
import {Context} from '../../index';
import ItemCard from './ItemCard';
import SpinnerDark from '../UI/SpinnerDark';
import Seo from '../seo/Seo';
import {CATEGORY_INFINITE_PAGE_SIZE, SHOP_ITEM_GRID_CLASS} from '../../util/Constants';
import {catalogItemId, hasCatalogItemId} from '../../util/catalogItemId';
import {useDebounced} from '../../hooks/useDebounced';
import {ShopFiltersSection, SHOP_SORT_OPTIONS} from './ShopFiltersSection';

const UNCATEGORIZED = 'Без категории';

function itemCategoryTitle(item) {
    const t = item.categoryResponse?.title;
    return typeof t === 'string' && t.trim() ? t.trim() : '';
}

const Catalog = observer(() => {
    const {items: itemsStore} = useContext(Context);
    const [sort, setSort] = useState('title,asc');
    const [query, setQuery] = useState('');
    const [minPrice, setMinPrice] = useState('');
    const [maxPrice, setMaxPrice] = useState('');
    const [inStockOnly, setInStockOnly] = useState(true);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [sortOpen, setSortOpen] = useState(false);

    const debouncedQuery = useDebounced(query, 350);

    /** Загрузка по категориям: порядок как в API категорий, пагинация отдельно на категорию. */
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState('');
    const [categoriesReady, setCategoriesReady] = useState(false);

    const sentinelRef = useRef(null);
    const filterTokenRef = useRef(0);
    const sectionsRef = useRef(sections);
    sectionsRef.current = sections;

    useEffect(() => {
        let cancelled = false;
        if (itemsStore.categories.length > 0) {
            setCategoriesReady(true);
            return () => {
                cancelled = true;
            };
        }
        fetchDataUnauth(CATEGORIES)
            .then((data) => {
                if (!cancelled) {
                    itemsStore.setCategories(Array.isArray(data) ? data : []);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    itemsStore.setCategories([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setCategoriesReady(true);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [itemsStore]);

    const categoryTitles = useMemo(
        () => itemsStore.categories.map((c) => c?.title).filter((t) => typeof t === 'string' && t.trim()),
        [itemsStore.categories],
    );

    const categoryTitlesKey = useMemo(() => categoryTitles.join('\u0001'), [categoryTitles]);

    const buildParamsForCategory = useCallback(
        (categoryTitle, pageIndex) => {
            const params = {
                page: pageIndex,
                size: CATEGORY_INFINITE_PAGE_SIZE,
                sort,
                onlyAvailable: inStockOnly,
            };
            if (categoryTitle != null && categoryTitle !== '') {
                params.category = categoryTitle;
            }
            const q = debouncedQuery.trim();
            if (q) params.query = q;
            const min = minPrice === '' ? null : Number(minPrice);
            const max = maxPrice === '' ? null : Number(maxPrice);
            if (min != null && !Number.isNaN(min) && min >= 0) params.minPrice = min;
            if (max != null && !Number.isNaN(max) && max >= 0) params.maxPrice = max;
            return params;
        },
        [sort, debouncedQuery, minPrice, maxPrice, inStockOnly],
    );

    useEffect(() => {
        if (!categoriesReady) {
            return undefined;
        }

        filterTokenRef.current += 1;
        const token = filterTokenRef.current;
        setError('');
        setLoading(true);

        if (categoryTitles.length === 0) {
            setSections([{title: null, items: [], page: 0, exhausted: false}]);
            fetchItemsPage(buildParamsForCategory(null, 0))
                .then((data) => {
                    if (token !== filterTokenRef.current) return;
                    const chunk = data.items || [];
                    setSections([
                        {
                            title: null,
                            items: chunk,
                            page: 0,
                            exhausted: Boolean(data.last) || chunk.length === 0,
                        },
                    ]);
                })
                .catch(() => {
                    if (token !== filterTokenRef.current) return;
                    setError('Не удалось загрузить каталог.');
                    setSections([]);
                })
                .finally(() => {
                    if (token !== filterTokenRef.current) return;
                    setLoading(false);
                });
            return undefined;
        }

        const initial = categoryTitles.map((title) => ({
            title,
            items: [],
            page: 0,
            exhausted: false,
        }));
        setSections(initial);

        Promise.all(
            categoryTitles.map((title, idx) =>
                fetchItemsPage(buildParamsForCategory(title, 0)).then((data) => ({idx, data})),
            ),
        )
            .then((results) => {
                if (token !== filterTokenRef.current) return;
                setSections((prev) => {
                    const next = [...prev];
                    for (const {idx, data} of results) {
                        const chunk = data.items || [];
                        next[idx] = {
                            ...next[idx],
                            items: chunk,
                            page: 0,
                            exhausted: Boolean(data.last) || chunk.length === 0,
                        };
                    }
                    return next;
                });
            })
            .catch(() => {
                if (token !== filterTokenRef.current) return;
                setError('Не удалось загрузить каталог.');
                setSections([]);
            })
            .finally(() => {
                if (token !== filterTokenRef.current) return;
                setLoading(false);
            });

        return undefined;
    }, [categoriesReady, categoryTitlesKey, buildParamsForCategory, categoryTitles]);

    const allExhausted = sections.length > 0 && sections.every((s) => s.exhausted);

    const loadNextPage = useCallback(() => {
        if (loading || loadingMore || allExhausted) return;
        const snap = sectionsRef.current;
        const i = snap.findIndex((s) => !s.exhausted);
        if (i === -1) return;

        const sec = snap[i];
        const titleKey = sec.title;
        const nextPage = sec.page + 1;
        const token = filterTokenRef.current;

        setLoadingMore(true);
        fetchItemsPage(buildParamsForCategory(titleKey, nextPage))
            .then((data) => {
                if (token !== filterTokenRef.current) return;
                const chunk = data.items || [];
                setSections((prev) => {
                    const j = prev.findIndex((s) => s.title === titleKey);
                    if (j === -1) return prev;
                    const row = prev[j];
                    return prev.map((r, k) =>
                        k === j
                            ? {
                                  ...r,
                                  items: chunk.length > 0 ? [...row.items, ...chunk] : row.items,
                                  page: nextPage,
                                  exhausted: Boolean(data.last) || chunk.length === 0,
                              }
                            : r,
                    );
                });
            })
            .catch(() => {
                if (token !== filterTokenRef.current) return;
                setSections((prev) =>
                    prev.map((r) =>
                        r.title === titleKey ? {...r, exhausted: true} : r,
                    ),
                );
            })
            .finally(() => {
                if (token !== filterTokenRef.current) return;
                setLoadingMore(false);
            });
    }, [loading, loadingMore, allExhausted, buildParamsForCategory]);

    const totalCount = useMemo(() => sections.reduce((s, x) => s + x.items.length, 0), [sections]);

    useEffect(() => {
        const el = sentinelRef.current;
        if (!el || allExhausted || loading) return undefined;
        const obs = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) {
                    loadNextPage();
                }
            },
            {root: null, rootMargin: '160px', threshold: 0},
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, [loadNextPage, allExhausted, loading, totalCount]);

    const groupedSections = useMemo(() => {
        if (sections.length === 1 && sections[0].title == null) {
            const items = sections[0].items;
            if (items.length === 0) return [];
            const byCat = new Map();
            for (const item of items) {
                const key = itemCategoryTitle(item) || UNCATEGORIZED;
                if (!byCat.has(key)) byCat.set(key, []);
                byCat.get(key).push(item);
            }
            const seen = new Set();
            const ordered = [];
            for (const c of itemsStore.categories) {
                const t = c?.title;
                if (t && byCat.has(t)) {
                    ordered.push(t);
                    seen.add(t);
                }
            }
            const rest = [...byCat.keys()]
                .filter((k) => !seen.has(k))
                .sort((a, b) => a.localeCompare(b, 'ru'));
            return [...ordered, ...rest].map((title) => ({title, items: byCat.get(title)}));
        }

        return sections
            .filter((s) => s.items.length > 0)
            .map((s) => ({title: s.title, items: s.items}));
    }, [sections, itemsStore.categories]);

    return (
        <div className="pb-8">
            <Seo
                title="Ароматические свечи — каталог Lutica"
                description="Каталог ароматических соевых свечей ручной работы Lutica: купить натуральные ароматические свечи для дома. Фильтры по цене и наличию, разделы по категориям, оформление онлайн, доставка по России."
                pathname="/catalog"
            />
            <ShopFiltersSection
                leading={
                    <h1 className="text-xl font-semibold tracking-tight text-bark-900 sm:text-2xl">
                        Каталог
                        <span className="sr-only"> ароматических свечей Lutica</span>
                    </h1>
                }
                sort={sort}
                setSort={setSort}
                sortOptions={SHOP_SORT_OPTIONS}
                filtersOpen={filtersOpen}
                setFiltersOpen={setFiltersOpen}
                sortOpen={sortOpen}
                setSortOpen={setSortOpen}
                query={query}
                setQuery={setQuery}
                minPrice={minPrice}
                setMinPrice={setMinPrice}
                maxPrice={maxPrice}
                setMaxPrice={setMaxPrice}
                inStockOnly={inStockOnly}
                setInStockOnly={setInStockOnly}
                idPrefix="catalog"
            />

            {!categoriesReady || loading ? (
                <div className="mt-10">
                    <SpinnerDark/>
                </div>
            ) : error ? (
                <div className="lut-card mt-8 text-red-800">{error}</div>
            ) : totalCount === 0 ? (
                <p className="mt-10 text-bark-600">По заданным условиям товаров не найдено.</p>
            ) : (
                <>
                    <div className="mt-8 space-y-10">
                        {groupedSections.map(({title, items}, idx) => (
                            <section key={title} aria-labelledby={`catalog-cat-${idx}`}>
                                <h2
                                    id={`catalog-cat-${idx}`}
                                    className="mb-4 text-center text-xl font-semibold tracking-tight text-bark-900 sm:text-2xl"
                                >
                                    {title}
                                </h2>
                                <div className={SHOP_ITEM_GRID_CLASS}>
                                    {items.filter(hasCatalogItemId).map((item) => (
                                        <ItemCard key={catalogItemId(item)} item={item}/>
                                    ))}
                                </div>
                            </section>
                        ))}
                    </div>
                    {!allExhausted && (
                        <div ref={sentinelRef} className="flex min-h-14 items-center justify-center py-4">
                            {loadingMore ? (
                                <div className="h-8 w-8 animate-spin rounded-full border-2 border-pine-200 border-t-pine-700"/>
                            ) : null}
                        </div>
                    )}
                </>
            )}
        </div>
    );
});

export default Catalog;
